import React, { useState } from 'react';
import { Phone, Mail, MessageCircle, Globe, Menu, X } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { translations } from '../translations/translations';
import { contactInfo } from '../data/mockData';
import { Button } from './ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';

const Header = () => {
  const { language, changeLanguage } = useLanguage();
  const t = translations[language];
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const languages = [
    { code: 'en', name: 'English', flag: '🇬🇧' },
    { code: 'ar', name: 'العربية', flag: '🇮🇶' },
    { code: 'ku', name: 'کوردی', flag: '🟨🟥🟩' },
  ];

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setMobileMenuOpen(false);
    }
  };

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-[#1a1d2e]/95 backdrop-blur-sm border-b border-[#c4a35a]/20">
      {/* Top Bar */}
      <div className="bg-[#0f1117] border-b border-[#c4a35a]/10">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between py-2 text-sm">
            <div className="flex items-center gap-4">
              <a href={`tel:${contactInfo.phones[0]}`} className="flex items-center gap-2 text-gray-300 hover:text-[#c4a35a] transition-colors">
                <Phone className="w-4 h-4" />
                <span className="hidden sm:inline">{contactInfo.phones[0]}</span>
              </a>
              <a href={`mailto:${contactInfo.email}`} className="flex items-center gap-2 text-gray-300 hover:text-[#c4a35a] transition-colors">
                <Mail className="w-4 h-4" />
                <span className="hidden md:inline">{contactInfo.email}</span>
              </a>
            </div>
            <div className="flex items-center gap-3">
              <a
                href={`https://wa.me/${contactInfo.whatsapp}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 px-3 py-1 bg-[#c4a35a] text-[#0f1117] rounded-full hover:bg-[#d4b36a] transition-colors text-xs font-medium"
              >
                <MessageCircle className="w-3 h-3" />
                <span className="hidden sm:inline">{t.whatsapp}</span>
              </a>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="sm" className="text-gray-300 hover:text-[#c4a35a] hover:bg-transparent">
                    <Globe className="w-4 h-4 me-1" />
                    <span className="text-xs">{languages.find(l => l.code === language)?.name}</span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="bg-[#1a1d2e] border-[#c4a35a]/20">
                  {languages.map((lang) => (
                    <DropdownMenuItem
                      key={lang.code}
                      onClick={() => changeLanguage(lang.code)}
                      className={`cursor-pointer hover:bg-[#c4a35a]/10 ${language === lang.code ? 'bg-[#c4a35a]/20 text-[#c4a35a]' : 'text-gray-300'}`}
                    >
                      <span className="me-2">{lang.flag}</span>
                      {lang.name}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </div>
      </div>

      {/* Main Navigation */}
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between py-4">
          {/* Logo */}
          <div className="flex items-center gap-3">
            <div className="relative w-12 h-12 rounded-full overflow-hidden bg-white border-2 border-[#c4a35a] flex-shrink-0">
              <img
                src="/logo.png"
                alt="Noble Law Firm"
                className="w-full h-full object-cover"
                onError={(e) => {
                  e.target.style.display = 'none';
                }}
              />
            </div>
            <div>
              <h1 className="text-xl font-bold text-[#c4a35a]">{t.company}</h1>
              <p className="text-xs text-gray-400">{t.companyTagline}</p>
            </div>
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-6">
            <button onClick={() => scrollToSection('home')} className="text-[#1a1d2e] hover:text-[#c4a35a] transition-colors font-medium">
              {t.home}
            </button>
            <button onClick={() => scrollToSection('about')} className="text-[#1a1d2e] hover:text-[#c4a35a] transition-colors font-medium">
              {t.about}
            </button>
            <button onClick={() => scrollToSection('services')} className="text-[#1a1d2e] hover:text-[#c4a35a] transition-colors font-medium">
              {t.services}
            </button>
            <button onClick={() => scrollToSection('team')} className="text-[#1a1d2e] hover:text-[#c4a35a] transition-colors font-medium">
              {t.team}
            </button>
            <button onClick={() => scrollToSection('contact')} className="text-[#1a1d2e] hover:text-[#c4a35a] transition-colors font-medium">
              {t.contact}
            </button>
            <Button
              onClick={() => scrollToSection('contact')}
              className="bg-[#c4a35a] text-[#0f1117] hover:bg-[#d4b36a] font-medium"
            >
              {t.bookConsultation}
            </Button>
          </nav>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="lg:hidden text-gray-300 hover:text-[#c4a35a] transition-colors"
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Navigation */}
        {mobileMenuOpen && (
          <nav className="lg:hidden py-4 border-t border-[#c4a35a]/20 bg-white">
            <div className="flex flex-col gap-3">
              <button onClick={() => scrollToSection('home')} className="text-[#1a1d2e] hover:text-[#c4a35a] transition-colors font-medium text-start py-2">
                {t.home}
              </button>
              <button onClick={() => scrollToSection('about')} className="text-[#1a1d2e] hover:text-[#c4a35a] transition-colors font-medium text-start py-2">
                {t.about}
              </button>
              <button onClick={() => scrollToSection('services')} className="text-[#1a1d2e] hover:text-[#c4a35a] transition-colors font-medium text-start py-2">
                {t.services}
              </button>
              <button onClick={() => scrollToSection('team')} className="text-[#1a1d2e] hover:text-[#c4a35a] transition-colors font-medium text-start py-2">
                {t.team}
              </button>
              <button onClick={() => scrollToSection('contact')} className="text-[#1a1d2e] hover:text-[#c4a35a] transition-colors font-medium text-start py-2">
                {t.contact}
              </button>
              <Button
                onClick={() => scrollToSection('contact')}
                className="bg-[#c4a35a] text-[#0f1117] hover:bg-[#d4b36a] font-medium w-full mt-2"
              >
                {t.bookConsultation}
              </Button>
            </div>
          </nav>
        )}
      </div>
    </header>
  );
};

export default Header;
